/**
 * AgenticMasteryModulePage — /agentic-ai-mastery/module/:slug
 *
 * Fetches module detail from GET /api/v1/agentic-mastery/modules/{slug}.
 *
 * Unlock logic:
 *   - If locked === false → render full markdown via <LessonMarkdown>
 *   - If locked === true  → show preview text + paywall card linking to /pricing
 *
 * Prev/next navigation uses prevSlug/nextSlug from the API response.
 */

import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FlagIcon from "@mui/icons-material/Flag";
import LockIcon from "@mui/icons-material/Lock";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getAgenticModule,
  getModuleFormative,
  getModuleResources,
  completeModule,
  uncompleteModule,
  AgenticModuleDetail,
  ModuleManifestSegment,
  ModuleFormative,
  ModuleResources,
} from "../api/client";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";
import LessonMarkdown from "../components/LessonMarkdown";
import AgenticQuizBlock from "../components/AgenticQuizBlock";
import DebugExercisesBlock from "../components/DebugExercisesBlock";
import SegmentPracticeCard from "../components/SegmentPracticeCard";
import FormativeCheckBlock from "../components/FormativeCheckBlock";
import HomeworkResourcesBlock from "../components/HomeworkResourcesBlock";
import AiTutorWidget from "../components/AiTutorWidget";
import ReportIssueFab from "../components/ReportIssueFab";
import SectionSlides from "../components/SectionSlides";
import ModuleTOC from "../components/ModuleTOC";
import { useModuleSegments } from "../hooks/useModuleSegments";
import { Trans, useTranslation } from "react-i18next";
import { useDeckGrant } from "../hooks/useDeckGrant";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

export default function AgenticMasteryModulePage() {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const tierAccess = getTierAccess(user);
  const queryClient = useQueryClient();

  // Local optimistic state for the completed toggle.
  // null = defer to module.completed from API; true/false = local override.
  const [localCompleted, setLocalCompleted] = useState<boolean | null>(null);
  const [completing, setCompleting] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  const {
    data: module,
    isLoading,
    error,
  } = useQuery<AgenticModuleDetail>({
    queryKey: ["agentic-module", slug],
    queryFn: () => getAgenticModule(slug!),
    enabled: !!slug,
    retry: false,
  });

  // Intra-module TOC segments (markdown-derived) + the server manifest (v2).
  // Called before any early return to respect the rules of hooks.
  const { segments, manifest } = useModuleSegments(module?.markdown, slug);

  // Formative check (v2) — fetched here for its anchor so we can inject the
  // interactive widget after the "## Formative Check" heading. Shares the query
  // key with FormativeCheckBlock, so it's a single fetch.
  const { data: formative } = useQuery<ModuleFormative | null>({
    queryKey: ["formative", slug],
    queryFn: () => getModuleFormative(slug!),
    enabled: !!slug,
    retry: false,
  });
  const formativeAnchor = formative?.anchor || null;

  // Downloadable homework resources (v2) — fetched here for the anchor so the
  // download block can be injected at the homework heading.
  const { data: resources } = useQuery<ModuleResources | null>({
    queryKey: ["resources", slug],
    queryFn: () => getModuleResources(slug!),
    enabled: !!slug,
    retry: false,
  });
  const resourcesAnchor = resources?.anchor || null;

  // v2: per-segment practice data from the manifest. A check tagged to several
  // segments is "owned" by its FIRST segment only (interactive widgets must
  // render once). `practiceAnchors` are the segments that have anything to show
  // (plus the formative-check anchor, whose widget is injected the same way).
  const { practiceAnchors, ownedChecksByAnchor, manifestByAnchor } = useMemo(() => {
    const owned: Record<string, string[]> = {};
    const byAnchor: Record<string, ModuleManifestSegment> = {};
    const anchors: string[] = [];
    if (manifest) {
      const seen = new Set<string>();
      for (const seg of manifest.segments) {
        byAnchor[seg.anchor] = seg;
        const ownedIds = (seg.check_ids ?? []).filter((cid) => !seen.has(cid));
        ownedIds.forEach((cid) => seen.add(cid));
        owned[seg.anchor] = ownedIds;
        const hasAssets =
          ownedIds.length > 0 ||
          (seg.walkthrough_fns?.length ?? 0) > 0 ||
          (seg.slides?.length ?? 0) > 0 ||
          !!seg.has_lab;
        if (hasAssets) anchors.push(seg.anchor);
      }
    }
    if (formativeAnchor && !anchors.includes(formativeAnchor)) {
      anchors.push(formativeAnchor);
    }
    if (resourcesAnchor && !anchors.includes(resourcesAnchor)) {
      anchors.push(resourcesAnchor);
    }
    return { practiceAnchors: anchors, ownedChecksByAnchor: owned, manifestByAnchor: byAnchor };
  }, [manifest, formativeAnchor, resourcesAnchor]);

  const hasInlineSlides = useMemo(
    () => (manifest?.segments ?? []).some((s) => (s.slides?.length ?? 0) > 0),
    [manifest],
  );

  // A gated (non-free, unlocked) module with inline slides must mint the
  // deck-access grant cookie before its slide <img> tags fire — the /decks/
  // route is tier-gated and that grant used to be minted by <EmbeddedSlideDeck>,
  // which inline-slides modules suppress entirely (see hasInlineSlides above).
  // `module` is undefined while the query is loading, so needsGrant stays
  // false until it resolves; by the time SectionSlides is actually rendered
  // below, the isLoading/locked early-returns guarantee module is loaded and
  // unlocked, so this recomputes correctly once data lands. A locked module
  // shows the paywall instead of slides, so it never needs a grant.
  const needsGrant = hasInlineSlides && !!module && module.tier !== "free" && !module.locked;
  const { deckReady } = useDeckGrant(module?.slug, { needsGrant });

  const renderSegmentSlides = useCallback(
    (anchor: string) => {
      if (!deckReady) return null;
      const seg = manifestByAnchor[anchor];
      const slides = seg?.slides ?? [];
      return slides.length ? <SectionSlides slides={slides} /> : null;
    },
    [manifestByAnchor, deckReady],
  );

  const renderSegmentPractice = useCallback(
    (anchor: string) => {
      if (!slug) return null;
      // The formative-check heading gets the interactive self-check widget.
      if (formativeAnchor && anchor === formativeAnchor) {
        return <FormativeCheckBlock slug={slug} />;
      }
      // The homework heading gets the downloadable scaffold/dataset, plus its
      // practice card (lab/walkthrough) if the manifest has assets there.
      if (resourcesAnchor && anchor === resourcesAnchor) {
        const seg = manifestByAnchor[anchor];
        return (
          <>
            <HomeworkResourcesBlock slug={slug} />
            {seg && (
              <SegmentPracticeCard
                slug={slug}
                segment={seg}
                ownedCheckIds={ownedChecksByAnchor[anchor] ?? []}
              />
            )}
          </>
        );
      }
      const seg = manifestByAnchor[anchor];
      if (!seg) return null;
      return (
        <SegmentPracticeCard
          slug={slug}
          segment={seg}
          ownedCheckIds={ownedChecksByAnchor[anchor] ?? []}
        />
      );
    },
    [manifestByAnchor, ownedChecksByAnchor, slug, formativeAnchor, resourcesAnchor],
  );

  // ---- Loading state -------------------------------------------------------
  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 12, textAlign: "center" }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t("AgenticMasteryModulePage.loadingModule")}
        </Typography>
      </Container>
    );
  }

  // ---- Error state ---------------------------------------------------------
  if (error || !module) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {(error as any)?.response?.status === 404
            ? t("AgenticMasteryModulePage.moduleNotFoundError")
            : t("AgenticMasteryModulePage.loadModuleFailedError")}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/agentic-ai-mastery")}>
          {t("AgenticMasteryModulePage.backToAgenticMasteryButton")}
        </Button>
      </Container>
    );
  }

  // ---- Derived completion state -------------------------------------------
  // Use local override when the user has just toggled; else fall back to API.
  const isCompleted =
    localCompleted !== null ? localCompleted : (module?.completed ?? false);

  async function handleMarkComplete() {
    if (!slug) return;
    setCompleting(true);
    setCompleteError(null);
    try {
      await completeModule(slug);
      setLocalCompleted(true);
      // Invalidate both queries so the home page reflects the change.
      queryClient.invalidateQueries({ queryKey: ["agentic-progress"] });
      queryClient.invalidateQueries({ queryKey: ["agentic-module", slug] });
    } catch {
      setCompleteError(t("AgenticMasteryModulePage.markCompleteFailedError"));
    } finally {
      setCompleting(false);
    }
  }

  async function handleUnmark() {
    if (!slug) return;
    setCompleting(true);
    setCompleteError(null);
    try {
      await uncompleteModule(slug);
      setLocalCompleted(false);
      queryClient.invalidateQueries({ queryKey: ["agentic-progress"] });
      queryClient.invalidateQueries({ queryKey: ["agentic-module", slug] });
    } catch {
      setCompleteError(t("AgenticMasteryModulePage.unmarkFailedError"));
    } finally {
      setCompleting(false);
    }
  }

  // ---- Page ----------------------------------------------------------------
  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 4, md: 6 },
        }}
      >
        <Container maxWidth="md">
          {/* Breadcrumb */}
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/agentic-ai-mastery")}
              size="small"
              sx={{ color: "text.secondary", textTransform: "none", fontWeight: 500 }}
            >
              {t("AgenticMasteryModulePage.breadcrumbAgenticMastery")}
            </Button>
            <Typography variant="body2" color="text.disabled">
              ›
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("AgenticMasteryModulePage.moduleNumberBreadcrumb", { number: module.number })}
            </Typography>
          </Stack>

          {/* Chips */}
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={0.5}>
            <Chip
              label={t("AgenticMasteryModulePage.moduleNumberChip", { number: module.number })}
              size="small"
              sx={{
                fontWeight: 500,
                fontSize: "0.72rem",
                letterSpacing: "0.07em",
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
              }}
            />
            {module.locked ? (
              <Chip
                icon={<LockIcon sx={{ fontSize: "0.8rem !important" }} />}
                label={t("AgenticMasteryModulePage.allAccessChipLabel")}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                }}
              />
            ) : (
              <Chip
                label={t("AgenticMasteryModulePage.freeChipLabel")}
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  color: theme.palette.success.dark,
                }}
              />
            )}
            <Chip
              label={module.studyTime}
              size="small"
              variant="outlined"
              icon={<ScheduleIcon sx={{ fontSize: "0.85rem !important" }} />}
              sx={{ fontSize: "0.7rem" }}
            />
          </Stack>

          {/* Title + tagline */}
          <Typography
            variant="h3"
            fontWeight={500}
            sx={{ letterSpacing: "-0.02em", mb: 1 }}
          >
            {module.title}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 720, lineHeight: 1.65 }}
          >
            {module.tagline}
          </Typography>

          {/* Milestone badge */}
          {module.milestone?.title && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 2, color: "text.disabled" }}
            >
              {module.milestone.title}
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Learning outcomes — always shown. Capped to a readable column even
            though the surrounding container is wide for the two-column body. */}
        {module.learningOutcomes.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              borderRadius: 3,
              p: 3,
              mb: 5,
              maxWidth: 860,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <FlagIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: theme.palette.primary.main, letterSpacing: "0.05em", textTransform: "uppercase" }}
              >
                {t("AgenticMasteryModulePage.learningOutcomesEyebrow")}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {module.learningOutcomes.map((outcome, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ color: theme.palette.primary.main, minWidth: 24 }}
                  >
                    {i + 1}.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.65, flex: 1 }}>
                    {outcome}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        {module.locked ? (
          // ----------------------------------------------------------------
          // Locked: show preview + paywall card
          // ----------------------------------------------------------------
          <Box sx={{ maxWidth: 860 }}>
            {module.preview && (
              <Box mb={5}>
                <LessonMarkdown>{module.preview}</LessonMarkdown>
              </Box>
            )}

            {tierAccess.isPro ? <ProUpsellNote /> : (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: 3,
                }}
              >
                <LockIcon sx={{ fontSize: 40, color: alpha(theme.palette.primary.main, 0.6), mb: 2 }} />
                <Typography variant="h6" fontWeight={500} gutterBottom>
                  {t("AgenticMasteryModulePage.partOfAllAccessHeading")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, maxWidth: 480, mx: "auto" }}
                >
                  {t("AgenticMasteryModulePage.partOfAllAccessBody")}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/pricing")}
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    fontWeight: 500,
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                  }}
                >
                  {t("AgenticMasteryModulePage.upgradeToAllAccessButton")}
                </Button>
              </Paper>
            )}
          </Box>
        ) : (
          // ----------------------------------------------------------------
          // Unlocked: two-column layout — main lesson column + sticky TOC.
          //   md+   : grid with a sticky TOC sidebar on the right.
          //   xs/sm : single column; TOC renders as a horizontal chip row
          //           above the body (inside the main column).
          // ----------------------------------------------------------------
          <Box
            sx={{
              display: "grid",
              gap: { xs: 0, md: 5 },
              gridTemplateColumns: {
                xs: "1fr",
                md: "minmax(0, 1fr) 240px",
              },
              alignItems: "start",
            }}
          >
            {/* Main column */}
            <Box sx={{ minWidth: 0 }}>
              {/* Mobile-only chip TOC (hidden md+ inside the component). */}
              <ModuleTOC segments={segments} variant="chips" />

              {/* Slides render inline per segment (below), via renderSegmentSlides. */}
              {module.markdown ? (
                <LessonMarkdown
                  practiceAnchors={practiceAnchors}
                  renderSegmentPractice={renderSegmentPractice}
                  renderSegmentSlides={renderSegmentSlides}
                >
                  {module.markdown}
                </LessonMarkdown>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t("AgenticMasteryModulePage.comingSoonAlert")}
                </Alert>
              )}

              {/* Knowledge-check quiz — silently absent when unavailable or locked */}
              <AgenticQuizBlock slug={module.slug} />

            {hasInlineSlides && deckReady && (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 4 }}>
                <Typography variant="body2" color="text.secondary">{t("AgenticMasteryModulePage.slidesForModuleLabel")}</Typography>
                <Button size="small" variant="outlined" component="a"
                  href={`/decks/${module.slug}/slides/deck.pdf`} download
                  startIcon={<PictureAsPdfIcon />} sx={{ textTransform: "none" }}>
                  {t("AgenticMasteryModulePage.downloadPdfButton")}
                </Button>
                <Button size="small" variant="text" component="a"
                  href={`/decks/${module.slug}/slides/deck.pptx`} download
                  sx={{ textTransform: "none" }}>
                  {t("AgenticMasteryModulePage.downloadPptxButton")}
                </Button>
              </Stack>
            )}

            {/* Natural-language "debug this code" exercises. With a manifest the
                checks are interleaved inline per segment above, so this flat
                list is a FALLBACK only (manifest missing / older modules). */}
            {!manifest && <DebugExercisesBlock slug={module.slug} />}

            {/* Scoped AI Tutor — floating widget, unlocked modules only */}
            <AiTutorWidget slug={module.slug} moduleTitle={module.title} />

            {/* Report an issue — opens a pre-filled GitHub issue for this module */}
            <ReportIssueFab slug={module.slug} moduleTitle={module.title} />

            {/* Progress control — auth-gated */}
            <Box mt={5}>
              {isAuthenticated ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: `1px solid ${
                      isCompleted
                        ? alpha(theme.palette.success.main, 0.3)
                        : alpha(theme.palette.primary.main, 0.2)
                    }`,
                    borderRadius: 3,
                    bgcolor: isCompleted
                      ? alpha(theme.palette.success.main, 0.03)
                      : alpha(theme.palette.primary.main, 0.02),
                    transition: "all 0.25s",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      {isCompleted ? (
                        <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                      ) : (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                          }}
                        />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {isCompleted
                            ? t("AgenticMasteryModulePage.moduleCompletedLabel")
                            : t("AgenticMasteryModulePage.markCompleteLabel")}
                        </Typography>
                        {isCompleted && (
                          <Typography variant="caption" color="text.secondary">
                            {t("AgenticMasteryModulePage.trackedProgressCaption")}
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      {completeError && (
                        <Typography variant="caption" color="error">
                          {completeError}
                        </Typography>
                      )}
                      {isCompleted ? (
                        <Tooltip title={t("AgenticMasteryModulePage.clickToUnmarkTooltip")}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={completing}
                            onClick={handleUnmark}
                            sx={{
                              borderColor: alpha(theme.palette.success.main, 0.5),
                              color: theme.palette.success.dark,
                              fontWeight: 500,
                              textTransform: "none",
                              "&:hover": {
                                borderColor: theme.palette.success.main,
                                bgcolor: alpha(theme.palette.success.main, 0.04),
                              },
                            }}
                          >
                            {completing
                              ? t("AgenticMasteryModulePage.savingLabel")
                              : t("AgenticMasteryModulePage.completedCheckLabel")}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={completing}
                          onClick={handleMarkComplete}
                          startIcon={
                            completing ? (
                              <CircularProgress size={14} sx={{ color: "inherit" }} />
                            ) : undefined
                          }
                          sx={{
                            bgcolor: theme.palette.primary.main,
                            fontWeight: 500,
                            textTransform: "none",
                            "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                          }}
                        >
                          {completing
                            ? t("AgenticMasteryModulePage.savingLabel")
                            : t("AgenticMasteryModulePage.markAsCompleteButton")}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              ) : (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: "block", textAlign: "center" }}
                >
                  <Trans
                    i18nKey="AgenticMasteryModulePage.signInToTrackProgress"
                    t={t}
                    components={[
                      <Button
                        key="signin0"
                        size="small"
                        variant="text"
                        onClick={() =>
                          navigate("/login", {
                            state: { from: `/agentic-ai-mastery/module/${slug}` },
                          })
                        }
                        sx={{
                          color: "text.disabled",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          p: 0,
                          minWidth: 0,
                          "&:hover": { color: theme.palette.primary.main, bgcolor: "transparent" },
                        }}
                      />,
                    ]}
                  />
                </Typography>
              )}
              </Box>
            </Box>
            {/* End main column */}

            {/* Sticky TOC sidebar — md+ only (component hides itself on xs/sm). */}
            <ModuleTOC segments={segments} variant="sidebar" />
          </Box>
        )}

        {/* Prev / Next navigation */}
        <Divider sx={{ my: 5 }} />
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ maxWidth: 860 }}
        >
          {module.prevSlug ? (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() =>
                navigate(`/agentic-ai-mastery/module/${module.prevSlug}`)
              }
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                textAlign: "left",
                flex: 1,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.disabled" display="block">
                  {t("AgenticMasteryModulePage.previousModuleLabel")}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {t("AgenticMasteryModulePage.moduleNumberLabel", { number: module.prevSlug.replace(/^m(\d+)-(\d+).*/, "$1.$2") })}
                </Typography>
              </Box>
            </Button>
          ) : (
            <Box flex={1} />
          )}

          {module.nextSlug ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() =>
                navigate(`/agentic-ai-mastery/module/${module.nextSlug}`)
              }
              sx={{
                textTransform: "none",
                justifyContent: "flex-end",
                textAlign: "right",
                bgcolor: theme.palette.primary.main,
                fontWeight: 500,
                flex: 1,
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.8)", display: "block" }}
                >
                  {t("AgenticMasteryModulePage.nextModuleLabel")}
                </Typography>
                <Typography variant="body2" fontWeight={500} color="white">
                  {t("AgenticMasteryModulePage.moduleNumberLabel", { number: module.nextSlug.replace(/^m(\d+)-(\d+).*/, "$1.$2") })}
                </Typography>
              </Box>
            </Button>
          ) : (
            // Last module — pointer back to home
            <Button
              variant="outlined"
              onClick={() => navigate("/agentic-ai-mastery")}
              sx={{ textTransform: "none", flex: 1, justifyContent: "center" }}
            >
              {t("AgenticMasteryModulePage.backToCurriculumButton")}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
